import Waylay from '@waylay/client'
import { ErrorFormat } from '@waylay/client/dist/util/axiosutil'
import { UsageClient } from './usageClient'
import { getNavigate } from '~/components/Common/NavigationWrapper'

// we have to parse the value as JSON because that's how it's stored by the
// use-persisted-state package
const token = window.localStorage.getItem('token')
const refreshToken = window.localStorage.getItem('refresh_token')

const { WAYLAY_API_GATEWAY_URL, WAYLAY_API_GATEWAY_ENFORCED } = window.env

const enforceGateway = WAYLAY_API_GATEWAY_ENFORCED
  ? WAYLAY_API_GATEWAY_ENFORCED !== 'false'
  : !!WAYLAY_API_GATEWAY_URL
const waylayOptions = {
  token,
  refreshToken,
  enforceGateway,
  gateway: WAYLAY_API_GATEWAY_URL,
  enforceConfigured: true as const,
  errorMessageFormat: ErrorFormat.Normal,
  auth: { onTokenRefresh },
  billing: {
    timeout: 20000,
  },
  etl: {
    timeout: 10000,
  },
  pubsub: {
    timeout: 60000,
  },
}
if (!enforceGateway) {
  // only for development overrides
  const {
    WAYLAY_API_URL,
    WAYLAY_ALARMS_URL,
    WAYLAY_DATA_URL,
    WAYLAY_VAULT_URL,
    WAYLAY_WEBSCRIPTS_URL,
    WAYLAY_AUTH_URL,
    WAYLAY_QUERIES_URL,
    WAYLAY_BYOML_URL,
    WAYLAY_ETL_URL,
    WAYLAY_REGISTRY_URL,
    WAYLAY_RESOURCES_URL,
    WAYLAY_USAGE_URL,
    WAYLAY_PUBSUB_URL,
    WAYLAY_STORAGE_URL,
    WAYLAY_HOARD_URL,
  } = window.env

  const urlOverrides = {
    auth: WAYLAY_AUTH_URL,
    data: WAYLAY_DATA_URL,
    alarms: WAYLAY_ALARMS_URL,
    webscripts: WAYLAY_WEBSCRIPTS_URL,
    vault: WAYLAY_VAULT_URL,
    queries: WAYLAY_QUERIES_URL,
    byoml: WAYLAY_BYOML_URL,
    registry: WAYLAY_REGISTRY_URL,
    resources: WAYLAY_RESOURCES_URL,
    types: WAYLAY_RESOURCES_URL,
    constraints: WAYLAY_RESOURCES_URL,
    billing: WAYLAY_USAGE_URL,
    etl: WAYLAY_ETL_URL,
    storage: WAYLAY_STORAGE_URL,
    hoard: WAYLAY_HOARD_URL,
    pubsub: WAYLAY_PUBSUB_URL,
    globalSettings: WAYLAY_API_URL,
  }
  Object.entries(urlOverrides).forEach(([key, url]) => {
    if (url) {
      if (waylayOptions[key]) waylayOptions[key].baseUrl = url
      else waylayOptions[key] = { baseUrl: url }
    }
  })
}

const client = new Waylay(waylayOptions)
const clientContainer = {
  client,
  usage: new UsageClient(client),
  pubsubWs: null,
}

clientContainer.pubsubWs = getPubsubWs({ raise: false })

function onTokenRefresh(err, tokens) {
  if (err) {
    console.error(err)
    window.localStorage.removeItem('token')
    window.localStorage.removeItem('refresh_token')
    location.reload() // this should log out the user when refreshing has failed
    return
  }

  const { token, refresh_token: refreshToken } = tokens
  window.localStorage.setItem('token', token)

  if (refreshToken) {
    window.localStorage.setItem('refresh_token', refreshToken)
  }
  // force refresh of the UI
  try {
    const navigate = getNavigate()
    navigate(`${location.pathname}${location.search}`)
  } catch {
    location.reload()
  }
}

// lazy fetch of pub-sub, partially depends on availability of token/domain
// (only an issue when not using gateway in a multi-tenant environment)
// TODO: make this (and other configuration code) depend on correct token initialization.
export function getPubsubWs({ raise, force = false } = { raise: true }) {
  if (!clientContainer.pubsubWs || force) {
    const { WAYLAY_PUBSUB_DOMAIN: pubsubDomain } = window.env
    return clientContainer.client.pubsub.ws({
      domain: pubsubDomain,
      raise,
    })
  }

  return clientContainer.pubsubWs
}

export function reconfigureClient(token, refreshToken) {
  clientContainer.client = new Waylay({
    ...waylayOptions,
    token,
    refreshToken,
  })
  clientContainer.usage = new UsageClient(clientContainer.client)
  clientContainer.pubsubWs = getPubsubWs({ raise: false, force: true })
}

export const statusIs2XX = (status: number) => status >= 200 && status < 300
export const statusBelow5XX = (status: number) => status >= 200 && status < 500
export const anyStatus = (status: number) => status >= 0

export default clientContainer
